import { z } from 'zod';

/** URL por defecto del servidor de juego en desarrollo local. */
export const DEFAULT_SERVER_URL = 'ws://localhost:2567';

/**
 * Acepta cualquier URL absoluta parseable.
 *
 * No se usa `z.url()` porque el servidor de juego se direcciona con los
 * esquemas `ws://` y `wss://`, no solo con HTTP.
 */
const UrlLikeSchema = z
  .string()
  .min(1)
  .refine((value) => URL.canParse(value), {
    message: 'Se espera una URL absoluta, por ejemplo `ws://localhost:2567`.',
  });

/**
 * Contrato de las variables de entorno del cliente.
 *
 * Vite solo expone al bundle las variables con prefijo `VITE_`.
 */
export const ClientEnvSchema = z.object({
  VITE_SERVER_URL: UrlLikeSchema.default(DEFAULT_SERVER_URL),
  /**
   * Base HTTP del servidor, solo si difiere de la derivada de
   * `VITE_SERVER_URL`. Normalmente no hace falta: `ws://` y `wss://` se
   * traducen a `http://` y `https://` sobre el mismo host y puerto.
   */
  VITE_SERVER_HTTP_URL: UrlLikeSchema.optional(),
});

export type ClientEnv = z.infer<typeof ClientEnvSchema>;

/**
 * Valida el entorno del cliente.
 *
 * Las claves vacias se tratan como ausentes para que un `.env` con
 * `VITE_SERVER_URL=` recaiga en el valor por defecto en lugar de fallar.
 */
export function parseClientEnv(source: Record<string, unknown> = import.meta.env): ClientEnv {
  const candidate: Record<string, unknown> = {};

  for (const key of Object.keys(ClientEnvSchema.shape)) {
    const value = source[key];
    if (value !== undefined && value !== '') {
      candidate[key] = value;
    }
  }

  return ClientEnvSchema.parse(candidate);
}
